import { TextField, Typography, useMediaQuery, useTheme } from '@mui/material';
import { StockCheckStatus } from './../../../enums/StockCheckStatus';
import { StockCheck, StockCheckItem } from './../../../app/services/api/generated';
import React, { useCallback, useMemo } from 'react';

type IProps = {
  item: StockCheckItem,
  uom: string,
  stockCheckDetail?: StockCheck,
  selectedZoneId: string,
  allZonesEnabled: boolean,
  handleQuantityChange: (itemId: number | undefined, valueId: number | undefined, newQuantityString: string) => void;
  handleQuantityBlur: (itemId: number | undefined, valueId: number | undefined, newQuantityString: string) => void;
}

const StockCountQuantityInput = React.memo(({ item, uom, stockCheckDetail, selectedZoneId, allZonesEnabled, handleQuantityChange, handleQuantityBlur }: IProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));

  const foundItem = useMemo(() => {
    const found = item?.itemValues?.find(value => value.uom === uom);
    console.log(`[Input] Item ${item?.itemNumber}, UOM ${uom}, foundItem:`, found, 'countQuantity:', found?.countQuantity);
    return found;
  }, [item?.itemValues, uom, item?.itemNumber]);

  const isDisabled = useMemo(() => {
    const statusCheck = stockCheckDetail?.status === StockCheckStatus.Pending;
    const zoneCheck = selectedZoneId === '-1' && !allZonesEnabled;
    const disabled = statusCheck || zoneCheck;
    console.log(`[Input Disabled] status=${stockCheckDetail?.status}, isPending=${statusCheck}, zoneId=${selectedZoneId}, allZonesEnabled=${allZonesEnabled}, zoneCheck=${zoneCheck}, DISABLED=${disabled}`);
    return disabled;
  }, [stockCheckDetail?.status, selectedZoneId, allZonesEnabled]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    handleQuantityChange(item.id, foundItem?.id, e.target.value);
  }, [handleQuantityChange, item.id, foundItem?.id]);

  const handleBlur = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    handleQuantityBlur(item.id, foundItem?.id, e.target.value);
  }, [handleQuantityBlur, item.id, foundItem?.id]);

  return (
    <div style={{
      width: '144px', marginLeft: '10px',
      marginRight: '10px', display: 'flex', alignItems: 'center',
    }}>
      <div>
        <span className="title"> {uom}</span>
        {uom == 'EA' ? (<div className="conversion-to-each"
          style={{ color: theme.palette.custom.gray[400], width: isMobile ? '60px' : '10px' }}></div>) :
          (<div className="conversion-to-each"
            style={{ color: theme.palette.custom.gray[400], width: '60px' }}>
            {foundItem && foundItem.conversionToEach &&
              <Typography variant='textXS'>({foundItem.conversionToEach} EA)</Typography>
            }</div>
          )}
      </div>

      {foundItem ? (
        <TextField
          className="quantity-field"
          type="text"
          size="small"
          name="quantity-input"
          variant="outlined"
          value={foundItem.countQuantity ?? ''}
          disabled={isDisabled}
          sx={{
            width: '73px', ml: '10px', backgroundColor: isDisabled
              ? theme.palette.custom.gray[200]
              : undefined,
          }}
          onChange={handleChange}
          onBlur={handleBlur}
        />
      ) : (
        <span
          className="quantity-field"
          style={{
            width: '73px',
            height: '40px',
            marginLeft: '10px',
            paddingTop: '8.5px',
            borderRadius: '4px',
            paddingBottom: '8.5px',
            paddingLeft: '14px',
            paddingRight: '14px',
            lineHeight: '23px',
          }}
        >---</span>
      )}
    </div>
  );
}, (prevProps, nextProps) => {
  // Find the specific value for this UOM
  const prevValue = prevProps.item?.itemValues?.find(v => v.uom === prevProps.uom);
  const nextValue = nextProps.item?.itemValues?.find(v => v.uom === nextProps.uom);

  // Compare the actual countQuantity value
  if (prevValue?.countQuantity !== nextValue?.countQuantity) {
    return false; // Re-render if value changed
  }

  // Compare other relevant props
  if (prevProps.stockCheckDetail?.status !== nextProps.stockCheckDetail?.status) {
    return false;
  }

  if (prevProps.selectedZoneId !== nextProps.selectedZoneId) {
    return false;
  }

  if (prevProps.allZonesEnabled !== nextProps.allZonesEnabled) {
    return false;
  }

  return true;
});

StockCountQuantityInput.displayName = 'StockCountQuantityInput';

export default StockCountQuantityInput;
